import { Injectable } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { LocalStorageKeys } from '../shared/models/local-storage.model';
import { BehaviorSubject, of } from 'rxjs';
import { LoginForm } from '../shared/models/auth/login-form.model';
import { Router } from '@angular/router';
import { SetNewPasswordForm } from '../shared/models/auth/set-new-password-form.model';
import { PasswordChangeForm } from '../shared/models/auth/password-change-form.model';
import { AlertService } from '../shared/services/alert.service';
import { Roles } from '../shared/models/auth/role.model';
import { Employee } from '../shared/models/employee.model';
import { UserService } from '../shared/services/user.service';
import { ToastrService } from 'ngx-toastr';
import { CacheControlService } from '../shared/services/cache-control.service';


@Injectable({
    providedIn: 'root'
})
export class AuthService {
    authEmployee$: BehaviorSubject<Employee> = new BehaviorSubject<Employee>(new Employee());
    private isLoggedInSubject = new BehaviorSubject<boolean>(false);
    isLoggedIn$ = this.isLoggedInSubject.asObservable();
    url: string = environment.url;

    constructor(
        private http: HttpClient,
        private router: Router,
        private userService: UserService,
        private toastrService: ToastrService,
        private cacheControl: CacheControlService
    ) {
        const authEmployee = this.userService.getCurrentUser();
        if (authEmployee && authEmployee.user && authEmployee.user.email) {
            this.authEmployee$.next(authEmployee);
            this.isLoggedInSubject.next(true);
        }
    }

    login(credentials: LoginForm) {

        localStorage.removeItem(LocalStorageKeys.Token);
        localStorage.removeItem(LocalStorageKeys.User);
        localStorage.removeItem(LocalStorageKeys.TwoFactorChallenge);
        return this.http.post(this.url + 'auth/login', credentials).pipe(
            switchMap((res: any) => {
                // 2FA wymagane / wymagany enrollment — nie zapisujemy tokena sesji,
                // tylko krótkotrwały challenge; routing obsługuje LoginComponent.
                if (res && res.twoFactorStatus) {
                    localStorage.setItem(LocalStorageKeys.TwoFactorChallenge, res.challengeToken);
                    return of({ twoFactorStatus: res.twoFactorStatus });
                }

                // Logowanie jednoskładnikowe — pełny JWT.
                localStorage.setItem(LocalStorageKeys.Token, JSON.stringify(res));
                return this.getCurrentUserDetails().pipe(
                    map(v => Employee.adapt(v)),
                    tap(v => {
                        if (v && v.user.email) {
                            localStorage.setItem(LocalStorageKeys.User, JSON.stringify(v));
                            this.authEmployee$.next(v);
                            this.isLoggedInSubject.next(true);
                        }
                    }),
                    map(() => ({ twoFactorStatus: 'none' })),
                );
            }),
        );
    }

    // --- 2FA ---

    /** Weryfikacja kodu podczas logowania → pełny JWT + pobranie profilu. */
    verifyTwoFactor(code: string) {
        const challengeToken = localStorage.getItem(LocalStorageKeys.TwoFactorChallenge);
        return this.http.post(this.url + 'auth/2fa/verify', { challengeToken, code }).pipe(
            switchMap((res: any) => this.completeTwoFactorLogin(res)),
        );
    }

    /** Enrollment wymuszony podczas logowania (autoryzacja challenge tokenem). */
    setupTwoFactorChallenge() {
        const challengeToken = localStorage.getItem(LocalStorageKeys.TwoFactorChallenge);
        return this.http.post(this.url + 'auth/2fa/setup', { challengeToken });
    }

    /**
     * Potwierdzenie enrollmentu kodem podczas logowania. Backend wystawia od razu
     * pełny JWT (hasło + kod z nowego urządzenia = pełne logowanie 2FA).
     */
    enableTwoFactorChallenge(code: string) {
        const challengeToken = localStorage.getItem(LocalStorageKeys.TwoFactorChallenge);
        return this.http.post(this.url + 'auth/2fa/enable', { challengeToken, code }).pipe(
            switchMap((res: any) => this.completeTwoFactorLogin(res)),
        );
    }

    /** Porzuca krok 2FA (np. wygasły challenge) i wraca na ekran logowania. */
    abandonTwoFactorChallenge(message: string) {
        localStorage.removeItem(LocalStorageKeys.TwoFactorChallenge);
        this.toastrService.error(message);
        this.router.navigate(['/login']);
    }

    private completeTwoFactorLogin(tokenResponse: any) {
        localStorage.setItem(LocalStorageKeys.Token, JSON.stringify(tokenResponse));
        localStorage.removeItem(LocalStorageKeys.TwoFactorChallenge);
        return this.getCurrentUserDetails().pipe(
            map(v => Employee.adapt(v)),
            tap(v => {
                if (v && v.user.email) {
                    localStorage.setItem(LocalStorageKeys.User, JSON.stringify(v));
                    this.authEmployee$.next(v);
                    this.isLoggedInSubject.next(true);
                }
            }),
        );
    }

    /** Opt-in z ustawień konta (autoryzacja zwykłym JWT). */
    setupTwoFactor() {
        return this.http.post(this.url + 'user/2fa/setup', {});
    }

    enableTwoFactor(code: string) {
        return this.http.post(this.url + 'user/2fa/enable', { code });
    }

    disableTwoFactor(code: string) {
        return this.http.post(this.url + 'user/2fa/disable', { code });
    }

    getCurrentUserDetails() {
        return this.http.get(this.url + 'profile');
    }

    hasRole(role: Roles) {
        const authEmployee = this.userService.getCurrentUser();
        let hasRole = false;
        if (authEmployee.user.roles && authEmployee.user.roles.includes(role)) {
            hasRole = true;
        }

        return hasRole;
    }

    logout() {
        // Clear all caches before logging out
        this.cacheControl.clearLocalCache();
        return this.http.post(this.url + 'auth/logout', { message: 'logout' })
            .pipe(
                tap(res => {
                    localStorage.removeItem(LocalStorageKeys.Token);
                    localStorage.removeItem(LocalStorageKeys.User);
                    this.isLoggedInSubject.next(false);
                }),
                tap(res => {
                    // this.alertService.setMessage('Zostałeś poprawnie wylogowany');
                    this.toastrService.success('Zostałeś poprawnie wylogowany')
                }),
                tap(res => {
                    this.router.navigate(['/login']);
                }),
                catchError(err => {
                    // this.alertService.setMessage('Wystąpił problem z wylogowaniem', AlertTypes.Error);
                    this.toastrService.error('Wystąpił problem z wylogowaniem');
                    return err;
                })
            );
    }

    remindPasswordSendEmail(email: string) {
        return this.http.post(this.url + 'user/password/reset', { email });

    }

    setNewPassword(setNewPasswordForm: SetNewPasswordForm) {
        return this.http.post(this.url + 'user/password/set', setNewPasswordForm);
    }

    changePassword(passwordChangeForm: PasswordChangeForm) {
        return this.http.post(this.url + 'user/credentials/change', passwordChangeForm);
    }

    changeEmail(newEmail: string) {
        return this.http.post(this.url + 'user/credentials/change', { newEmail });
    }

}